import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { ArrowLeft, Ban, Check, Copy, ShieldAlert, ShieldCheck, ShieldOff, Users } from 'lucide-react';
import { ApiError } from '../api/client';
import * as adminApi from '../api/admin';
import { useAuth } from '../auth/AuthContext';
import { AdminAccountSummary, AdminUser } from '../types/auth';

/**
 * Platform-wide user and account management. System administrator only — a global
 * capability unrelated to any account's roles, so it lives outside the per-account
 * Profile screen entirely. See SystemAdminAccessService on the server for the actual
 * gate; this page's own check is just so a non-admin doesn't see a page full of
 * requests that will be refused.
 */
export function Admin() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center">
        <p className="text-blue-300">Loading…</p>
      </div>
    );
  }

  if (!user?.systemAdmin) {
    return <Navigate to="/options" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
              <ShieldAlert className="w-7 h-7 text-amber-300" />
              System admin
            </h1>
            <p className="text-blue-300">
              Every registered person and program. Only you can see this.
            </p>
          </div>
          <Link
            to="/options"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </Link>
        </div>

        <UsersSection selfId={user.id} />
        <AccountsSection />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- users

function UsersSection({ selfId }: { selfId: string }) {
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [resetLink, setResetLink] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await adminApi.fetchAdminUsers());
      setError('');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function withRow(userId: string, action: () => Promise<void>) {
    setBusyId(userId);
    setError('');
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That action failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleResetLink(target: AdminUser) {
    setBusyId(target.id);
    setError('');
    setCopied(false);
    try {
      const { link } = await adminApi.issueAdminResetLink(target.id);
      setResetLink({ email: target.email, url: link });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create a reset link.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleCopy() {
    if (!resetLink) return;
    await navigator.clipboard.writeText(resetLink.url);
    setCopied(true);
  }

  const formatDate = (timestamp: number) =>
    new Date(timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
      <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-300" />
        Users
      </h2>
      <p className="text-sm text-blue-300/80 mb-4">
        Reset a forgotten password, disable a login, or grant someone else admin access.
      </p>

      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

      {resetLink && (
        <div className="mb-4 p-4 bg-amber-500/10 border border-amber-400/30 rounded-xl">
          <p className="text-sm text-white mb-2">
            Reset link for {resetLink.email} — valid for one hour. Send it to them
            directly; it isn't emailed automatically.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <code className="flex-1 min-w-[220px] text-xs text-amber-200 bg-black/20 rounded-lg px-3 py-2 break-all">
              {resetLink.url}
            </code>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-white px-3 py-2 rounded-lg text-sm transition-colors"
            >
              <Copy className="w-4 h-4" />
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={() => { setResetLink(null); setCopied(false); }}
              className="text-amber-200/70 hover:text-white text-sm px-2 py-2"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-blue-300">Loading…</p>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const isSelf = row.id === selfId;
            const busy = busyId === row.id;

            return (
              <div
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
              >
                <div className="min-w-[200px]">
                  <p className="text-white font-medium flex items-center gap-2">
                    {row.displayName}
                    {row.systemAdmin && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        System admin
                      </span>
                    )}
                    {!row.enabled && (
                      <span className="text-xs text-red-300">Disabled</span>
                    )}
                    {isSelf && <span className="text-xs text-blue-300/70">(you)</span>}
                  </p>
                  <p className="text-sm text-blue-300/80">{row.email}</p>
                  <p className="text-xs text-blue-300/50 mt-0.5">Registered {formatDate(row.createdAt)}</p>
                </div>

                <div className="flex items-center gap-2">
                  <IconButton
                    onClick={() => handleResetLink(row)}
                    disabled={busy}
                    title="Create a password-reset link"
                    tone="blue"
                  >
                    <Copy className="w-5 h-5" />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      withRow(row.id, () => adminApi.setUserEnabled(row.id, !row.enabled))
                    }
                    disabled={busy || isSelf}
                    title={isSelf ? "You cannot disable your own account" : row.enabled ? 'Disable login' : 'Re-enable login'}
                    tone={row.enabled ? 'red' : 'green'}
                  >
                    {row.enabled ? <Ban className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      withRow(row.id, () => adminApi.setUserSystemAdmin(row.id, !row.systemAdmin))
                    }
                    disabled={busy || isSelf}
                    title={
                      isSelf
                        ? 'You cannot change your own system-admin status'
                        : row.systemAdmin
                          ? 'Revoke system-admin access'
                          : 'Grant system-admin access'
                    }
                    tone="amber"
                  >
                    {row.systemAdmin ? <ShieldOff className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const TONE_CLASSES: Record<'blue' | 'red' | 'green' | 'amber', string> = {
  blue: 'text-blue-300 hover:text-blue-200 hover:bg-blue-500/15',
  red: 'text-red-300 hover:text-red-200 hover:bg-red-500/15',
  green: 'text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/15',
  amber: 'text-amber-300 hover:text-amber-200 hover:bg-amber-500/15',
};

function IconButton({
  onClick,
  disabled,
  title,
  tone,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  tone: 'blue' | 'red' | 'green' | 'amber';
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${TONE_CLASSES[tone]}`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------- accounts

function AccountsSection() {
  const [rows, setRows] = useState<AdminAccountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setRows(await adminApi.fetchAdminAccounts());
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Could not load accounts.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatDate = (timestamp: number) =>
    new Date(timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
      <h2 className="text-xl font-semibold text-white mb-1">Accounts</h2>
      <p className="text-sm text-blue-300/80 mb-4">
        Read-only — for finding which program a support request is about.
      </p>

      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

      {loading ? (
        <p className="text-blue-300">Loading…</p>
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <div
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
            >
              <div className="min-w-[200px]">
                <p className="text-white font-medium">{row.name}</p>
                <p className="text-sm text-blue-300/80">
                  {row.ownerDisplayName ?? 'No owner'} {row.ownerEmail && `(${row.ownerEmail})`}
                </p>
                <p className="text-xs text-blue-300/50 mt-0.5">Started {formatDate(row.createdAt)}</p>
              </div>
              <p className="text-sm text-blue-300/70">
                {row.memberCount} {row.memberCount === 1 ? 'member' : 'members'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
